/**
 * FieldSyncSheet (Inventory Screens Step 5.4 A-1 Gate 2) — read-only
 * sync-queue detail sheet, opened by a FieldSyncChip tap.
 *
 * Shell mechanics are a deliberate SIBLING copy of BottomSheetSelect's
 * D-5.3-8 nav/gesture contract — NOT an extraction (BottomSheetSelect sits
 * in the record-modal path; refactoring it is out of 5.4 A-1 scope):
 *   - own sentinel in location.state (tmgFieldSyncSheet, pushed by the host
 *     SPREAD over existing state), isOpen derived there
 *   - EVERY close path resolves through the host's single navigate(-1):
 *     backdrop tap, drag-down > 80px, Escape (capture + stopPropagation),
 *     hardware/gesture back (pops the entry; isOpen re-derives false)
 * The option-list internals are NOT forked — this is a read-only panel.
 *
 * Data: the shared zone-clean useFieldQueue subscription (A-2 reactivity
 * fix — the old useLiveQuery(getQueue) querier crossed a foreign await
 * before the read and froze at its mount snapshot) — rows appear/vanish
 * live as items enqueue, drain-mark, and reconcile-dequeue.
 * Three buckets:
 *   Pending         — status 'pending' (awaiting sync)
 *   Syncing         — status 'saved' (server-confirmed, finishing the local
 *                     reconcile — in flight)
 *   Needs attention — conflict / not_found / error (incl. duplicate) /
 *                     failed: review-gated, never dropped
 * Above the buckets sits an F-40b COUNT LINE for records holding unsent
 * edits (dirtyCount, threaded from the host's useFieldDrain). Not a bucket:
 * those records are not queue items and carry no status, retry or discard
 * action. It also suppresses 'All changes synced', which used to appear on
 * an empty queue even while the device sat on unsent work.
 *
 * Actions (A-Phase 2): Path 1 — a duplicate-tool_create error row gets
 * "Edit ID & retry" — inline controlled input (no modal library; the
 * zone's lightweight pattern), submit → retryDuplicateToolCreate (row write
 * then queue flip, outbox_id preserved, media stays chained on the
 * unchanged record_id) → grid thread via onRecordChange → manual
 * tmg-field-enqueued dispatch (the flip is not a producer put, so the
 * outbox does not self-nudge). Path 2 — a conflict field_capture row gets
 * "Re-apply my edits" → reapplyFieldCapture (re-base the row on server
 * truth, re-enqueue from it; the D-5 coalescer supersedes the conflict item
 * and the producer put self-nudges — no manual dispatch, no hand delete).
 * The server 412's fields[] renders as read-only D-4 context ("the office
 * changed these") — display only, it never drives the re-apply. Row removal
 * from needs-attention is NOT hand-managed — both resolutions are syncQueue
 * writes, so useLiveQuery re-buckets on its own. All other rows remain
 * read-only (remaining paths later).
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  retryDuplicateToolCreate, reapplyFieldCapture, retryFailedItem, discardFailedItem,
  enqueueToolCreate, getQueue, hasAnyToolCreate,
  FIELD_ENQUEUED_EVENT, ACTIONABLE_STATUSES,
} from '../sync/fieldOutbox';
import { isPendingRecord } from '../sync/pendingId';
import { useFieldQueue } from '../sync/useFieldQueue';
import { MAX_RETRIES } from '../sync/retryPolicy';
import { getStorageEstimate } from '../db/storageEstimate';
import './FieldSyncSheet.css';

const DRAG_CLOSE_PX = 80;
const MB = 1024 * 1024;

/** Bytes → "12.3 MB". One decimal; callers guarantee a real number. */
function mb(bytes) {
  return `${(bytes / MB).toFixed(1)} MB`;
}

/**
 * Queue payload size. The `?? blob_data?.length` fallback is LOAD-BEARING:
 * byte_size was added to enqueueMediaAdd after devices were already carrying
 * queued items, so pre-existing rows have no such field and the total must
 * still be correct on those. Non-media items contribute 0 (media_add is the
 * only bulk payload type).
 */
function queueBytes(items) {
  return items.reduce((sum, i) => sum + (i.byte_size ?? i.blob_data?.length ?? 0), 0);
}

const TYPE_LABELS = {
  field_capture: 'Field capture',
  tool_create: 'New tool',
  media_add: 'Photo',
  media_remove: 'Photo removal',
};

const ATTENTION_STATUSES = ['conflict', 'not_found', 'error', 'failed'];

// Type-less v1 items are field captures (drain back-compat contract).
function typeLabel(item) {
  return TYPE_LABELS[item.type] || TYPE_LABELS.field_capture;
}

// Path 2 failure surfaces (D-4): the rep must know a purge means there is
// nothing left to recover — never a silent no-op.
const REAPPLY_REASON_MSG = {
  row_absent: "This tool's local data is gone — nothing to re-apply. Re-Load the site.",
  not_found: "This tool's local data is gone — nothing to re-apply. Re-Load the site.",
  no_etag: "Couldn't read the current server version — try again.",
  empty_diff: 'No pending edits left on this record — nothing to re-apply.',
};
const REAPPLY_FALLBACK_MSG = 'Re-apply failed — try again.';

const FAILED_ACTION_ERROR_MSG = 'Could not retry — the item may have already changed. '
  + 'Close and reopen this sheet.';
// Same shape, honest verb: a refused discard has not retried anything.
const DISCARD_ACTION_ERROR_MSG = 'Could not discard — the item may have already changed. '
  + 'Close and reopen this sheet.';

// Phase 4A Send (orphan Unsent rows). The refusal is the render→tap race
// guard: a tool_create landed after this row rendered as an orphan. A throw
// means nothing was queued — every throw site in enqueueToolCreate (its
// guard, the put) and in the getQueue re-check sits before or at the write.
const SEND_ALREADY_QUEUED_MSG = 'Already queued — this tool is waiting to send.';
const SEND_ACTION_ERROR_MSG = 'Could not send — nothing was queued. Try again.';

// Path 3 discard confirmation, per type. These are NOT interchangeable: a
// media_add holds the only copy of a photo that never reached the server,
// and a media_remove is genuinely harmless (the photo simply stays). Telling
// the rep "this cannot be undone" on the harmless one trains them to ignore
// the warning on the destructive ones.
//
// Field cancel Phase 3: ANY item on a pending- record — a tool_create, or
// any other type — discards the WHOLE RECORD (discardFailedItem's one
// rule), so it gets pendingRecordDiscardWarning instead of its per-type
// line. A media_add row on a pending record must not read like a
// single-photo discard when the record goes with it. The old tool_create
// line ("deletes it permanently") was false while discard removed only the
// queue item; it is replaced, not kept.
const pendingRecordDiscardWarning = (item, queueItems) => {
  const sameRecord = queueItems.filter((i) => i.record_id === item.record_id);
  const create = sameRecord.find((i) => i.type === 'tool_create');
  const label = create?.supplier_tool_id ? `"${create.supplier_tool_id}"` : 'this new record';
  const photos = sameRecord.filter((i) => i.type === 'media_add').length;
  const lead = item.type === 'tool_create'
    ? `Discarding removes ${label} from this device.`
    : `Discarding this ${typeLabel(item).toLowerCase()} removes the whole record ${label} `
      + 'from this device, not just this item.';
  const photoClause = photos === 0 ? ''
    : ` ${photos === 1 ? 'Its 1 photo has' : `Its ${photos} photos have`} not been uploaded `
      + `and will be deleted — there is no other copy.`;
  return `${lead} It has not been created on the server.${photoClause} This cannot be undone.`;
};

const discardWarning = (item, queueItems) => {
  if (isPendingRecord(item.record_id)) return pendingRecordDiscardWarning(item, queueItems);
  return {
    media_add:
      'This photo has never reached the server. Discarding deletes the '
      + 'only copy. It is not saved anywhere else and cannot be recovered.',
    field_capture:
      'Discarding permanently deletes these unsaved edits. The record '
      + 'keeps whatever the server currently has.',
    media_remove:
      'Discarding cancels this pending photo deletion. The photo stays '
      + 'on the server. Nothing is lost.',
  }[item.type] || 'Discarding permanently deletes this queued change.';
};

// discardFailedItem refusals → what to tell the rep. Each reason means a
// different thing and wants different advice; none is folded into the
// generic DISCARD_ACTION_ERROR_MSG (which is reserved for a thrown failure).
const DISCARD_REASON_MSG = {
  absent: 'This item is already gone — nothing was discarded.',
  not_actionable: 'This item changed (it may be retrying) — nothing was discarded. Look again.',
  drain_active: 'A sync is running, so nothing was discarded. Try again when it finishes.',
  already_created: 'The server has already accepted this tool, so the record can’t be '
    + 'discarded here. It will reappear as a normal record the next time the site is loaded.',
  row_absent: 'This record is no longer on this device — nothing was discarded.',
  not_pending: 'This record has already synced — nothing was discarded.',
};

function fmtConflictVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function itemDetail(item) {
  if (item.type === 'tool_create') return item.supplier_tool_id || item.record_id;
  const id = item.tool_id || item.record_id || '';
  return isPendingRecord(id) ? 'awaiting tool creation' : id;
}

function attentionLabel(item) {
  if (item.status === 'conflict') return 'Conflict';
  if (item.status === 'not_found') return 'Tool not found';
  if (item.status === 'failed') return 'Failed after retries';
  // PHASE 1: a 403 lands as markCaptureResult(outboxId, 'error',
  // {error:'auth_denied'}) — drainFieldQueue.js:105-113. auth_denied is an
  // ERROR CODE on a status-'error' row, NOT a status of its own, so it
  // needs no ATTENTION_STATUSES entry ('error' is already listed) and the
  // generic 'Error' fallback below stays intact for every other code.
  if (item.error === 'auth_denied') return 'Access denied';
  if (item.error === 'duplicate_supplier_tool_id') return 'Duplicate Tool ID';
  if (item.error === 'tool_not_found') return 'Tool not found';
  return 'Error';
}

export default function FieldSyncSheet({
  isOpen, onClose, onRecordChange, callerEmail, dirtyCount = 0, halted = false,
  networkDown = false,
  // F-45: the device-wide unsent set, already projected and ordered by the
  // host ({record_id, site_id, site_name, label, state}, plus
  // supplier_tool_id/oem on state 'orphan' only — Phase 4A; the host's
  // unsentRowState is the one discriminator, this sheet only reads it). The
  // sheet holds no site state of its own and never queries — activeSiteId
  // arrives as a prop purely to decide which rows are tappable.
  unsentItems = [], activeSiteId = null, onUnsentTap,
  // Field cancel Phase 3: filter-ONLY host callback (dropRecord). NOT the
  // modal's handleRecordRemoved — that one pops history first, which here
  // would pop the sheet's own entry and close it.
  onRecordRemoved,
}) {
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(null);
  const items = useFieldQueue();

  // Path 1 inline editor (one open at a time, keyed by outbox_id)
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState(null);

  // Path 2 re-apply (busy + inline failure, keyed by outbox_id)
  const [reapplyBusyId, setReapplyBusyId] = useState(null);
  const [reapplyError, setReapplyError] = useState(null); // { id, msg } | null

  // Path 3 failed-item actions. One confirm open at a time (a single state
  // value IS the mutual exclusion — opening row B's confirm overwrites row
  // A's id, so A closes with no bookkeeping).
  const [confirmingDiscardId, setConfirmingDiscardId] = useState(null);
  const [failedBusyId, setFailedBusyId] = useState(null);
  // { id, msg } | null — KEYED like reapplyError, not a bare string: several
  // rows can be 'failed' at once, and an unkeyed message would paint the
  // same error under every one of them. (editError gets away with a bare
  // string only because editingId already scopes its block to one row.)
  const [failedError, setFailedError] = useState(null);

  // Phase 4A Send, keyed by record_id (Unsent rows have no outbox_id).
  // sendInFlightRef is the synchronous double-tap guard: sendBusyId only
  // disables the button after a re-render, and a second tap landing before
  // that render would otherwise run a second check-then-enqueue.
  const [sendBusyId, setSendBusyId] = useState(null);
  const [sendError, setSendError] = useState(null); // { id, msg } | null
  const sendInFlightRef = useRef(false);

  // Device storage headroom — CONSOLE ONLY, one-shot per sheet open.
  //
  // This was briefly a rendered line ("Device: 45 MB of 64 GB used") and was
  // removed: there is no decision a rep makes from an absolute figure. The
  // useful form is a near-full THRESHOLD WARNING, and that policy call (what
  // counts as near-full, what the rep is told to do) is deferred. Until then
  // the numbers are a developer question — specifically whether iOS standalone
  // reports usage/quota at all and at what magnitude — so they go to the
  // console, which is where that question gets answered.
  //
  // Deliberately NOT inside the liveQuery: estimate() is not reactive and
  // re-running it on every queue mutation would be pointless async churn.
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    getStorageEstimate().then((est) => {
      if (alive) console.log('[fieldSyncSheet] storage estimate:', est);
    });
    return () => { alive = false; };
  }, [isOpen]);

  // Escape (desktop) — capture + stopPropagation: close the SHEET only
  // (mirrors BottomSheetSelect.jsx:50-60).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, onClose]);

  // Never carry a half-finished drag (or edit/re-apply) across open/close cycles.
  useEffect(() => {
    setDragY(0);
    dragStartY.current = null;
    setEditingId(null);
    setEditValue('');
    setEditError(null);
    setReapplyBusyId(null);
    setReapplyError(null);
    setConfirmingDiscardId(null);
    setFailedBusyId(null);
    setFailedError(null);
    setSendBusyId(null);
    setSendError(null);
  }, [isOpen]);

  // A destructive confirm must never outlive the row-state it was opened
  // against. There is no imperative queue reload to hook — useFieldQueue is
  // a Dexie useLiveQuery, so a fresh `items` identity IS the reload signal
  // (it re-emits on every syncQueue write: a drain mark, a reconcile
  // dequeue, another device's item landing, or our own retry/discard). Keyed
  // on `items` rather than a length or a status digest: any write at all is
  // enough reason to make the rep look again before deleting something
  // irreversible. useLiveQuery holds its result in state, so this fires on
  // emission, not on every render.
  //
  // failedError is the exception (field cancel Phase 3): it is cleared only
  // when the item it belongs to LEAVES items or CHANGES STATUS. A
  // drain_active refusal fires exactly while the drain is writing, so
  // clearing on every emission erased it before it could be read.
  useEffect(() => {
    setConfirmingDiscardId(null);
    setFailedBusyId(null);
    setFailedError((prev) => {
      if (!prev) return prev;
      const current = items.find((i) => i.outbox_id === prev.id);
      return current && current.status === prev.status ? prev : null;
    });
  }, [items]);

  if (!isOpen) return null;

  const pending = items.filter((i) => i.status === 'pending');
  const syncing = items.filter((i) => i.status === 'saved');
  const attention = items.filter((i) => ATTENTION_STATUSES.includes(i.status));

  // E5: ONE condition, two consumers — the banner and the Pending subtitle.
  // A bucket reading "sync stopped" while the banner was suppressed would
  // recreate in miniature the contradiction Phase C existed to remove.
  const showHalt = halted && !networkDown && (items.length > 0 || dirtyCount > 0);

  // B-017: ONE test for "this row renders the waiting text", read by the row
  // and by the Unsent header. The header's "open and Save to queue" is hidden
  // whenever any row renders that text: the two say opposite things, and the
  // waiting row would otherwise carry both. Hiding it never leaves the bucket
  // without an instruction, because every Unsent row renders one of its own:
  // "tap to open and Save", "not sent yet — tap Send", the waiting text, or
  // "<site> — open that site to Save".
  const rendersWaitingText = (u) => u.state === 'waiting' && u.site_id === activeSiteId;
  const showUnsentSaveNote = !unsentItems.some(rendersWaitingText);

  const handleDragStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleDragMove = (e) => {
    if (dragStartY.current == null) return;
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current));
  };
  const handleDragEnd = () => {
    if (dragStartY.current == null) return;
    const shouldClose = dragY > DRAG_CLOSE_PX;
    dragStartY.current = null;
    setDragY(0);
    if (shouldClose) onClose();
  };

  // ── Path 1: Edit ID & retry (duplicate tool_create only) ────────────────
  const canRetryDuplicate = (item) => item.type === 'tool_create'
    && item.status === 'error' && item.error === 'duplicate_supplier_tool_id';

  // A retry that cannot succeed is worse than no button at all. Two
  // shapes qualify. A duplicate tool_create has Path 1's "Edit ID &
  // retry" as the only retry that can work — a plain replay re-sends
  // the same supplier_tool_id and 409s again. A not_found row means the
  // server point-read came back empty; replaying the same request finds
  // the same nothing. Discard stays in both cases: abandoning the row is
  // still a legitimate exit. Append new futile shapes here, not at the
  // render site, so the next one cannot drift.
  const isRetryFutile = (item) =>
    canRetryDuplicate(item) || item.status === 'not_found';

  // ── Path 2: Re-apply my edits (conflict field_capture only) ─────────────
  const canReapply = (item) => item.type === 'field_capture'
    && item.status === 'conflict';

  // ── Path 3: Retry / Discard (any terminal status, ALL four types) ────────
  // Status-only: the type never narrows it. D-39 widened the set from the
  // single 'failed' to ACTIONABLE_STATUSES — imported, never re-compared
  // inline, so this gate can never drift from the two library guards it
  // fronts (see the constant's own comment for why that matters).
  const canActOnFailed = (item) => ACTIONABLE_STATUSES.includes(item.status);

  // Sole action — no accept-server/keep-mine fork: re-apply IS
  // accept-server-then-overlay-mine (D-4 blind-win falls out of the
  // re-base). Success needs no dispatch (producer put self-nudges, Gate 3)
  // and no hand removal (the D-5 coalescer deleted the conflict item; the
  // liveQuery re-buckets). Failure surfaces inline — never silent.
  const submitReapply = async (item) => {
    setReapplyBusyId(item.outbox_id);
    setReapplyError(null);
    try {
      const { reapplied, reason, row } = await reapplyFieldCapture(item, callerEmail);
      if (reapplied) {
        if (row) onRecordChange?.(row); // grid thread (shell upsert)
      } else {
        setReapplyError({
          id: item.outbox_id,
          msg: REAPPLY_REASON_MSG[reason] || REAPPLY_FALLBACK_MSG,
        });
      }
    } catch (err) {
      // Point-read transport/HTTP throw — nothing was mutated (write-last
      // ordering inside the re-base); safe to just try again.
      console.error('[fieldSyncSheet] Re-apply failed:', err);
      setReapplyError({ id: item.outbox_id, msg: REAPPLY_REASON_MSG.no_etag });
    } finally {
      setReapplyBusyId(null);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.outbox_id);
    setEditValue(String(item.supplier_tool_id ?? ''));
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditError(null);
  };

  // Order (spec): validate → record-row write → queue flip (both inside the
  // helper, row first) → grid thread → drain nudge. The liveQuery over
  // syncQueue re-buckets the row out of needs-attention on the flip — no
  // hand-managed removal.
  const submitEdit = async (item) => {
    setEditBusy(true);
    setEditError(null);
    try {
      const { retried, row } = await retryDuplicateToolCreate(item, editValue);
      if (!retried) return; // backstop — the disabled gate is the primary guard
      if (row) onRecordChange?.(row); // grid thread (shell upsert)
      // The flip is not a producer put — nudge the drain explicitly.
      window.dispatchEvent(new Event(FIELD_ENQUEUED_EVENT));
      cancelEdit();
    } catch (err) {
      console.error('[fieldSyncSheet] Edit ID & retry failed:', err);
      setEditError('Retry failed — try again');
    } finally {
      setEditBusy(false);
    }
  };

  // ── Path 3a: Retry a failed item ────────────────────────────────────────
  // retryFailedItem REBUILDS the item from the enqueue allowlist (put, not
  // merge) so no sender leftover rides along on the replay. It deliberately
  // does not self-nudge — the status flip is not a producer put — so the
  // dispatch below is the same manual nudge submitEdit does after
  // retryDuplicateToolCreate. No queue refresh call exists or is needed:
  // useFieldQueue's liveQuery re-buckets the row out of needs-attention on
  // the write itself.
  const handleRetryFailed = async (item) => {
    setFailedBusyId(item.outbox_id);
    setFailedError(null);
    try {
      const ok = await retryFailedItem(item.outbox_id);
      if (!ok) {
        setFailedError({ id: item.outbox_id, status: item.status, msg: FAILED_ACTION_ERROR_MSG });
        return;
      }
      window.dispatchEvent(new Event(FIELD_ENQUEUED_EVENT));
    } catch (err) {
      console.error('[fieldSyncSheet] Retry failed item failed:', err);
      setFailedError({ id: item.outbox_id, status: item.status, msg: FAILED_ACTION_ERROR_MSG });
    } finally {
      setFailedBusyId(null);
    }
  };

  // ── Path 3b: Discard a failed item ──────────────────────────────────────
  // Pure local dequeue — nothing was ever enqueued server-side, so there is
  // NO tmg-field-enqueued dispatch here (contrast Path 3a). The liveQuery
  // drops the row(s) on the delete.
  //
  // Field cancel Phase 3: on a pending- record the whole RECORD goes
  // (scope 'record'). The queue side is live, but the host's records list is
  // a one-shot snapshot, so onRecordRemoved (the host's filter-only
  // dropRecord) tells it — otherwise the grid keeps a ghost row.
  //
  // No open-modal orphan is possible from here: the sheet and the record
  // modal are never mounted together. The sync chip is z-index 30
  // (--z-popover) under ModalShell's --z-overlay backdrop, so it cannot be
  // tapped while a modal is open; and D-138's handleUnsentTap push DROPS
  // tmgFieldSyncSheet as it adds the modal sentinel. Independent; both hold.
  const handleDiscardFailed = async (item) => {
    setFailedBusyId(item.outbox_id);
    setFailedError(null);
    try {
      const res = await discardFailedItem(item.outbox_id);
      if (!res.discarded) {
        setFailedError({
          id: item.outbox_id,
          status: item.status,
          msg: DISCARD_REASON_MSG[res.reason] || DISCARD_ACTION_ERROR_MSG,
        });
        return;
      }
      setConfirmingDiscardId(null);
      if (res.scope === 'record') onRecordRemoved?.(item.record_id);
    } catch (err) {
      console.error('[fieldSyncSheet] Discard failed item failed:', err);
      setFailedError({ id: item.outbox_id, status: item.status, msg: DISCARD_ACTION_ERROR_MSG });
    } finally {
      setFailedBusyId(null);
    }
  };

  // ── Phase 4A: Send an orphan (pending- row, no tool_create at all) ──────
  // Rendered only on the host's 'orphan' state, so `u` carries the fields
  // the host read off the record row. Two steps, and only two:
  //   1. Re-check against the queue AS IT IS NOW (getQueue, a fresh read),
  //      not the `items` this row rendered from. hasAnyToolCreate is
  //      status-blind: a failed or conflicting create is still a create,
  //      and Needs attention owns it.
  //   2. The one existing create path, enqueueToolCreate. It writes only
  //      the queue item — the row is already on disk — and it self-nudges
  //      the drain via notifyEnqueued(), so there is NO manual
  //      FIELD_ENQUEUED_EVENT dispatch here (contrast Path 3a's Retry,
  //      whose status flip is not a producer put).
  // No success state to manage: the new item re-emits useFieldQueue, the
  // host re-classifies the row 'waiting', and Send disappears on its own.
  const handleSendOrphan = async (u) => {
    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    setSendBusyId(u.record_id);
    setSendError(null);
    try {
      const current = await getQueue();
      if (hasAnyToolCreate(current, u.record_id)) {
        setSendError({ id: u.record_id, msg: SEND_ALREADY_QUEUED_MSG });
        return;
      }
      await enqueueToolCreate({
        record_id: u.record_id,
        site_id: u.site_id,
        supplier_tool_id: u.supplier_tool_id,
        oem: u.oem,
        caller_email: callerEmail ?? null,
      });
    } catch (err) {
      console.error('[fieldSyncSheet] Send failed:', err);
      setSendError({ id: u.record_id, msg: SEND_ACTION_ERROR_MSG });
    } finally {
      sendInFlightRef.current = false;
      setSendBusyId(null);
    }
  };

  const renderBucket = (title, note, bucketItems, isAttention = false) => {
    if (bucketItems.length === 0) return null;
    return (
      <div className="fss-bucket">
        <div className={`fss-bucket-header${isAttention ? ' fss-bucket-header-attention' : ''}`}>
          <span className="fss-bucket-title">{title}</span>
          <span className="fss-bucket-note">{note}</span>
          <span className="fss-bucket-count">{bucketItems.length}</span>
        </div>
        {bucketItems.map((item) => (
          <React.Fragment key={item.outbox_id}>
            <div className="fss-item">
              <div className="fss-item-main">
                <span className="fss-item-type">{typeLabel(item)}</span>
                <span className="fss-item-detail">{itemDetail(item)}</span>
              </div>
              {isAttention && (
                <span className="fss-item-status">{attentionLabel(item)}</span>
              )}
              {/* PHASE 1 (retry visibility): the !isAttention and
                  status === 'pending' conjuncts are GONE. A retry count is
                  a fact about the ITEM, not about which bucket it landed
                  in, and the row that most needed it — one held in
                  needs-attention — was the only row that could never show
                  it. Rendered as a SIBLING of the attentionLabel span
                  above, never in place of it, so an attention row now
                  carries both (e.g. "Access denied" + "retry 2/3"). The
                  > 0 suppression stays: "retry 0/3" is noise. */}
              {(item.retry_count || 0) > 0 && (
                <span className="fss-item-status">retry {item.retry_count}/{MAX_RETRIES}</span>
              )}
              {isAttention && canRetryDuplicate(item) && editingId !== item.outbox_id && (
                <button
                  type="button"
                  className="fss-action-btn"
                  data-testid="fss-edit-retry"
                  onClick={() => startEdit(item)}
                >
                  Edit ID &amp; retry
                </button>
              )}
              {isAttention && canReapply(item) && (
                <button
                  type="button"
                  className="fss-action-btn"
                  data-testid="fss-reapply"
                  disabled={reapplyBusyId === item.outbox_id}
                  onClick={() => submitReapply(item)}
                >
                  {reapplyBusyId === item.outbox_id ? 'Re-applying…' : 'Re-apply my edits'}
                </button>
              )}
              {isAttention && canActOnFailed(item) && confirmingDiscardId !== item.outbox_id && (
                <>
                  {/* Which shapes are futile — and why — lives on
                      isRetryFutile, next to canRetryDuplicate. Kept out of
                      this render site on purpose: the reasoning belongs in
                      one place or the next addition drifts from it. */}
                  {!isRetryFutile(item) && (
                    <button
                      type="button"
                      className="fss-action-btn"
                      data-testid="fss-retry-failed"
                      disabled={failedBusyId === item.outbox_id}
                      onClick={() => handleRetryFailed(item)}
                    >
                      {failedBusyId === item.outbox_id ? 'Retrying…' : 'Retry'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="fss-action-btn"
                    data-testid="fss-discard-failed"
                    onClick={() => setConfirmingDiscardId(item.outbox_id)}
                  >
                    Discard
                  </button>
                </>
              )}
            </div>
            {isAttention && canActOnFailed(item) && confirmingDiscardId === item.outbox_id && (
              /* Inline confirm — no modal, no portal (the zone's lightweight
                 pattern, and a phone must not lose the row it is acting on).
                 The warning is rendered TEXT, never a title attribute: there
                 is no hover on a phone, so a tooltip would be invisible
                 exactly where the destruction happens. */
              <div className="fss-edit-row" data-testid="fss-discard-confirm-block">
                <span className="fss-edit-error">{discardWarning(item, items)}</span>
                <button
                  type="button"
                  className="fss-edit-confirm"
                  data-testid="fss-discard-confirm"
                  disabled={failedBusyId === item.outbox_id}
                  onClick={() => handleDiscardFailed(item)}
                >
                  {failedBusyId === item.outbox_id ? 'Discarding…' : 'Discard permanently'}
                </button>
                <button
                  type="button"
                  className="fss-edit-cancel"
                  data-testid="fss-discard-cancel"
                  disabled={failedBusyId === item.outbox_id}
                  onClick={() => setConfirmingDiscardId(null)}
                >
                  Cancel
                </button>
              </div>
            )}
            {failedError?.id === item.outbox_id && (
              <div className="fss-edit-row" data-testid="fss-failed-error">
                <span className="fss-edit-error">{failedError.msg}</span>
              </div>
            )}
            {isAttention && canReapply(item)
              && Array.isArray(item.fields) && item.fields.length > 0 && (
              /* D-4 read-only context — the server 412's attempted-vs-current.
                 Display only: re-apply sources from the re-based row + working,
                 never from this list. Empty fields[] (child-only stale arm)
                 renders nothing — the button above still works. */
              <div className="fss-conflict-context" data-testid="fss-conflict-context">
                <div className="fss-conflict-title">The office changed these while you were out:</div>
                {item.fields.map((f) => (
                  <div key={f.field} className="fss-conflict-row">
                    <span className="fss-conflict-field">{f.field}</span>
                    <span className="fss-conflict-val">their value: {fmtConflictVal(f.current)}</span>
                    <span className="fss-conflict-val fss-conflict-val-mine">your value: {fmtConflictVal(f.attempted)}</span>
                  </div>
                ))}
              </div>
            )}
            {reapplyError?.id === item.outbox_id && (
              <div className="fss-edit-row" data-testid="fss-reapply-error">
                <span className="fss-edit-error">{reapplyError.msg}</span>
              </div>
            )}
            {isAttention && editingId === item.outbox_id && (
              <div className="fss-edit-row" data-testid="fss-edit-row">
                <input
                  className="fss-edit-input"
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="New Tool ID"
                  aria-label="New Tool ID"
                  autoFocus
                />
                <button
                  type="button"
                  className="fss-edit-confirm"
                  data-testid="fss-edit-confirm"
                  disabled={editBusy
                    || !editValue.trim()
                    || editValue.trim() === String(item.supplier_tool_id ?? '').trim()}
                  onClick={() => submitEdit(item)}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="fss-edit-cancel"
                  onClick={cancelEdit}
                  disabled={editBusy}
                >
                  Cancel
                </button>
                {editError && <span className="fss-edit-error">{editError}</span>}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="field-sync-sheet">
      <div className="fss-backdrop" data-testid="fss-backdrop" onClick={onClose} />
      <div
        className="fss-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Sync status"
        style={dragY > 0 ? { transform: `translateY(${dragY}px)`, animation: 'none' } : undefined}
      >
        <div
          className="fss-grab"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onTouchCancel={handleDragEnd}
        >
          <div className="fss-handle" aria-hidden="true" />
          <div className="fss-title">Sync status</div>
        </div>
        <div className="fss-body">
          {showHalt && (
            /* Phase C. The queue gate is load-bearing, not defensive: `halted`
               can be PERMANENTLY stale. setHalted fires only when a
               callerEmail exists, and snapshot-authenticated mode has none by
               construction, so a flag lit in that mode never clears. Ungated,
               this banner would sit directly above the "All changes synced."
               empty state and contradict it. Gated, it can only appear when
               there is work that has actually stopped moving.
               No action offered, deliberately: after a 401 the drain re-sends
               a byte-identical header, because acquireTokenSilent is
               cache-first and none of the refusal causes (stale audience,
               wrong env, unmatched identity) are visible to MSAL. A retry
               control would reproduce the halt with no visible change, so the
               copy names the one thing that does work instead.
               Phase E: also suppressed while networkDown. Offline, the offline
               cause dominates and "sign in again" is the wrong remedy —
               possibly an impossible one. This mirrors FieldSyncChip's ladder,
               which already evaluates !navigator.onLine ABOVE halted. */
            <div className="fss-halt-banner" data-testid="fss-halt-banner">
              <div className="fss-halt-banner-title">Sync stopped</div>
              <div className="fss-halt-banner-body">
                Your work is saved on this device — nothing has been lost.
                Sign in again to resume.
              </div>
            </div>
          )}
          {items.length > 0 && (
            /* Queue weight only. Suppressed on an empty queue: "Queued: 0.0 MB"
               says nothing and competes with the fss-empty message below. */
            <div className="fss-item-detail" data-testid="fss-storage">
              {`Queued: ${mb(queueBytes(items))}`}
            </div>
          )}
          {unsentItems.length > 0 && (
            /* F-45: the count line is now an ENUMERATION. F-40b's original
               reasoning — that a dirty record is not a queue item and must
               not advertise Retry/Discard it does not have — still holds, and
               is why this borrows the bucket SHAPE but not renderBucket
               itself: that helper keys on outbox_id and branches on queue
               status, neither of which a record row has. The one action a
               dirty record does have is "open it", so that is the only
               affordance rendered, and only where it actually works.
               The count survives in the header: naming the records without
               saying how many would trade one omission for another. */
            <div className="fss-bucket" data-testid="fss-unsent">
              <div className="fss-bucket-header fss-bucket-header-unsent">
                <span className="fss-bucket-title">Unsent</span>
                {showUnsentSaveNote && <span className="fss-bucket-note">open and Save to queue</span>}
                <span className="fss-bucket-count">{unsentItems.length}</span>
              </div>
              {unsentItems.map((u) => (
                <React.Fragment key={u.record_id}>
                {u.state === 'orphan' ? (
                  /* Phase 4A: a pending- row with no tool_create in ANY
                     status — nothing will ever send it. Send, not Retry:
                     there was never a first attempt. A <div> row carrying a
                     button, the queue-row shape, NOT the tappable <button>
                     row: a button cannot nest in a button, and "open and
                     Save" would be false here anyway — Save on a pending- row
                     is refused (enqueueFieldCapture, D-5.2b-E). */
                  <div className="fss-item fss-unsent-row" data-testid="fss-unsent-row">
                    <span className="fss-item-main">
                      <span className="fss-item-type">{u.label}</span>
                      <span className="fss-item-detail">not sent yet — tap Send</span>
                    </span>
                    <button
                      type="button"
                      className="fss-action-btn"
                      data-testid="fss-unsent-send"
                      disabled={sendBusyId === u.record_id}
                      onClick={() => handleSendOrphan(u)}
                    >
                      {sendBusyId === u.record_id ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                ) : u.site_id === activeSiteId ? (
                  /* Same site: the row is in the host's `records` array, so
                     the tap can resolve it. A <button>, not a div with a
                     handler — it has to be reachable by keyboard and announce
                     itself as a control. */
                  <button
                    type="button"
                    className="fss-item fss-unsent-row fss-unsent-row-tappable"
                    data-testid="fss-unsent-row"
                    onClick={() => onUnsentTap && onUnsentTap(u)}
                  >
                    <span className="fss-item-main">
                      <span className="fss-item-type">{u.label}</span>
                      {/* B5: a 'waiting' row is a new tool whose create is
                          already on its way. Save on it is refused by design
                          (enqueueFieldCapture, D-5.2b-E) — its edits go up
                          with the create — so "open and Save" sent the rep to
                          a button that reports nothing. Still tappable: the
                          record opens and can be edited. True in every status
                          of that create, including one in Needs attention,
                          which is why it does not say "waiting to send". */}
                      <span className="fss-item-detail">
                        {rendersWaitingText(u) ? 'new tool — your edits send with it' : 'tap to open and Save'}
                      </span>
                    </span>
                  </button>
                ) : (
                  /* Another site: NOT tappable, and deliberately not a
                     disabled button either — there is no programmatic site
                     switcher in the zone, so the honest affordance is text
                     naming where the record lives. A dead control would read
                     as broken; this reads as an instruction. */
                  <div
                    className="fss-item fss-unsent-row"
                    data-testid="fss-unsent-row"
                  >
                    <span className="fss-item-main">
                      <span className="fss-item-type">{u.label}</span>
                      <span className="fss-item-detail">
                        {u.site_name} — open that site to Save
                      </span>
                    </span>
                  </div>
                )}
                {/* Keyed by record_id and rendered OUTSIDE the orphan branch
                    on purpose: the refusal fires exactly when the row is
                    about to re-classify 'waiting', and inside the branch the
                    message would vanish with it before it could be read. */}
                {sendError?.id === u.record_id && (
                  <div className="fss-edit-row" data-testid="fss-unsent-send-error">
                    <span className="fss-edit-error">{sendError.msg}</span>
                  </div>
                )}
                </React.Fragment>
              ))}
            </div>
          )}
          {items.length === 0 && dirtyCount === 0 && (
            /* F-40b: the `&& dirtyCount === 0` IS the honesty fix. An empty
               QUEUE is not synced work while records still hold unsent
               edits — an empty queue was the exact condition under which
               this line used to lie. */
            <div className="fss-empty" data-testid="fss-empty">All changes synced.</div>
          )}
          {renderBucket('Pending', showHalt ? 'sync stopped' : 'awaiting sync', pending)}
          {renderBucket('Syncing', 'in flight', syncing)}
          {renderBucket('Needs attention', 'held for review', attention, true)}
        </div>
      </div>
    </div>
  );
}
